import { ErrorBoundary } from '@/components/Rollbar';
import { DrawerCloseProvider } from '@/context/Drawer';
import { useReceivedInvoicesSearch } from '@/context/services/invoice/ReceivedInvoice.service';
import { ReceivedInvoicesSearchRefetchProvider } from '@/context/services/invoice/refetchContext';
import {
  InvoiceColumn,
  ReceivedInvoicesSearchQuery,
} from '@/context/services/invoice/type';
import { useReceivedInvoiceList } from '@/features/invoices/components/List/Table/hook';
import classnames from 'classnames/bind';
import {
  Dispatch,
  FC,
  SetStateAction,
  Suspense,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { Drawer } from './Drawer';
import styles from './List.module.scss';
import {
  ErrorInvoiceTable,
  InternalTableProps,
  InvoiceTable,
  LoadingInvoiceTable,
} from './Table';

const cx = classnames.bind(styles);

export type ReceivedInvoiceTableProps = InternalTableProps;

export type ReceivedInvoiceTableViewProps = {
  searchQuery: ReceivedInvoicesSearchQuery;
  onSearch: (value: ReceivedInvoicesSearchQuery) => void;
  isTriggeredSearch: boolean;
  setIsTriggeredSearch: Dispatch<SetStateAction<boolean>>;
  setIsPolling: Dispatch<SetStateAction<boolean>>;
  className: string;
};

const InnerList: FC<ReceivedInvoiceTableViewProps> = memo(
  ({
    searchQuery: _searchQuery,
    onSearch,
    isTriggeredSearch,
    setIsTriggeredSearch,
    setIsPolling,
    className,
  }) => {
    const { onDrawerClose, onDrawerOpen, selectedId } =
      useReceivedInvoiceList();
    const intervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const query = useMemo(() => {
      const { page, ...searchQuery } = _searchQuery;
      return { query: { ...searchQuery }, page };
    }, [_searchQuery]);

    const {
      data,
      pagination: _pagination,
      refetch,
    } = useReceivedInvoicesSearch(query);

    const table: ReceivedInvoiceTableProps = useMemo(() => {
      return {
        data,
        onRow: (data: InvoiceColumn) => {
          const isSelected =
            data.searchId.replace(/^IV-(\d+)$/, '$1') === selectedId;
          return {
            onClick: () => {
              onDrawerOpen(data.searchId);
            },
            className: isSelected ? 'selected-row' : '',
          };
        },
      };
    }, [data, onDrawerOpen, selectedId]);

    const onPageChange = useCallback(
      (page: number) => {
        onSearch({ page });
      },
      [onSearch]
    );

    const pagination = useMemo(() => {
      return {
        ..._pagination,
        onChange: onPageChange,
      };
    }, [_pagination, onPageChange]);

    const callRefetch = useCallback(() => {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
      }

      let retryCount = 0;
      const maxRetries = 5;

      const refetchWithTimeout = async () => {
        try {
          await refetch();
          intervalRef.current = setTimeout(refetchWithTimeout, 5000);
          setIsPolling(true);
        } catch (error) {
          if (retryCount < maxRetries) {
            retryCount++;
            const backoffTime = 1000 * Math.pow(2, retryCount);
            intervalRef.current = setTimeout(refetchWithTimeout, backoffTime);
          } else {
            if (intervalRef.current) {
              clearTimeout(intervalRef.current);
            }
          }
        }
      };

      refetchWithTimeout();
      setTimeout(() => {
        if (intervalRef.current) {
          clearTimeout(intervalRef.current);
        }
      }, 300000); //5minutes
    }, [refetch, setIsPolling]);

    useEffect(() => {
      const currentPage = _pagination?.currentPage;
      if (currentPage && currentPage !== _searchQuery.page) {
        onPageChange(currentPage);
      }
      if (isTriggeredSearch) {
        callRefetch();
        setIsTriggeredSearch(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [_pagination?.currentPage, isTriggeredSearch, callRefetch]);

    const isSearchEmpty = useMemo(() => {
      const { query: _query } = query;
      return data.length === 0 && Object.keys(_query).length !== 0;
    }, [data.length, query]);

    return (
      <ReceivedInvoicesSearchRefetchProvider value={refetch}>
        <div className={cx(className, styles['container'])}>
          <DrawerCloseProvider value={onDrawerClose}>
            <InvoiceTable
              pagination={pagination}
              table={table}
              isSearchEmpty={isSearchEmpty}
              callRefetch={callRefetch}
            />
            <Drawer selectedId={selectedId} onClose={onDrawerClose} />
          </DrawerCloseProvider>
        </div>
      </ReceivedInvoicesSearchRefetchProvider>
    );
  }
);
InnerList.displayName = 'InnerList';

export const List: FC<ReceivedInvoiceTableViewProps> = memo((props) => {
  return (
    <ErrorBoundary fallback={ErrorInvoiceTable}>
      <Suspense fallback={<LoadingInvoiceTable />}>
        <InnerList {...props} />
      </Suspense>
    </ErrorBoundary>
  );
});
List.displayName = 'List';
